import { motion } from 'framer-motion'
import { Apple, Cloud, Server, Mail, Phone } from 'lucide-react'
import { PageMeta } from '../components/seo/PageMeta'
import { Button } from '../components/ui/Button'
import { SectionLabel } from '../components/ui/SectionLabel'
import { FounderSection } from '../components/home/FounderSection'
import { pageMeta } from '../lib/seo'
import { aboutSchema } from '../lib/schema'

const certs = [
  { icon: Apple, name: 'Apple Certified', desc: 'Apple Business certified support' },
  { icon: Cloud, name: 'AWS Certified', desc: 'Amazon Web Services expertise' },
  { icon: Server, name: 'Microsoft 365 Partner', desc: 'Full M365 stack support' },
  { icon: Mail, name: 'Google Workspace Partner', desc: 'Google Workspace management' },
  { icon: Phone, name: 'RingCentral IGNITE Partner', desc: 'Unified communications' },
]

export default function About() {
  return (
    <>
      <PageMeta
        title={pageMeta.about.title}
        description={pageMeta.about.description}
        canonical={pageMeta.about.canonical}
        schema={aboutSchema}
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section style={{ background: 'var(--bg-2)', padding: '120px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <SectionLabel className="mb-6">Who We Are</SectionLabel>
            <h1
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(38px, 5vw, 56px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              Six people. 11 years. <span style={{ color: 'var(--accent)' }}>100+ businesses</span> that trust us.
            </h1>
            <p
              style={{
                fontSize: 16,
                color: 'var(--white-dim)',
                lineHeight: 1.75,
                maxWidth: 640,
              }}
            >
              NextTech Consultants is a distributed Pacific Northwest team — with members in Hillsboro, Tigard, Vancouver, McMinnville, and Newberg — bringing 11 years of experience to 100+ businesses across Oregon, Washington, and Idaho.
            </p>
          </div>
        </section>

        <FounderSection />

        {/* Stats strip */}
        <section style={{ background: 'var(--bg-2)', padding: '80px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <div className="flex flex-wrap justify-center gap-6 font-body" style={{ fontSize: 16, color: 'var(--white-dim)' }}>
              <span>100+ clients</span>
              <span style={{ color: 'var(--accent)' }}>·</span>
              <span>11 years</span>
              <span style={{ color: 'var(--accent)' }}>·</span>
              <span>97%+ retention</span>
              <span style={{ color: 'var(--accent)' }}>·</span>
              <span>6-person team</span>
              <span style={{ color: 'var(--accent)' }}>·</span>
              <span>99.9% uptime</span>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section style={{ background: 'var(--bg)', padding: '120px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <SectionLabel className="mb-6">Certifications</SectionLabel>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(32px, 4vw, 44px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 48,
              }}
            >
              Partner status with the tools your business already uses.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[3px]">
              {certs.map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="about-cert-card relative overflow-hidden transition-colors duration-250"
                  style={{
                    background: 'var(--bg-3)',
                    padding: '40px 32px',
                  }}
                >
                  <c.icon className="w-10 h-10 mb-4" style={{ color: 'var(--accent)' }} />
                  <h3
                    style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontWeight: 800,
                      fontSize: 20,
                      textTransform: 'uppercase',
                      color: 'var(--white)',
                      marginBottom: 8,
                    }}
                  >
                    {c.name}
                  </h3>
                  <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--white-dim)' }}>
                    {c.desc}
                  </p>
                </motion.div>
              ))}
            </div>
            <style dangerouslySetInnerHTML={{ __html: `
              .about-cert-card:hover { background: var(--bg-4) !important; }
              .about-cert-card::after {
                content: "";
                position: absolute;
                bottom: 0; left: 0;
                width: 100%; height: 2px;
                background: var(--accent);
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 0.3s ease;
              }
              .about-cert-card:hover::after { transform: scaleX(1); }
            ` }} />
          </div>
        </section>

        {/* CTA */}
        <section
          style={{
            background: 'var(--bg-2)',
            padding: '120px 0',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 48px' }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--white-dim)',
                marginBottom: 24,
                display: 'block',
              }}
            >
              Get Started
            </span>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(38px, 5vw, 52px)',
                textTransform: 'uppercase',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 20,
              }}
            >
              Ready to work with a team that <span style={{ color: 'var(--accent)' }}>gets it</span>?
            </h2>
            <p
              style={{
                fontSize: 16,
                color: 'var(--white-dim)',
                lineHeight: 1.7,
                marginBottom: 32,
              }}
            >
              Book a free 30-minute discovery call. No pressure — just a clear picture of what managed IT and AI automation could do for your business.
            </p>
            <Button to="/contact" variant="primary">
              Book Your Free Discovery Call
            </Button>
          </div>
        </section>
      </main>
    </>
  )
}
export { About as Component }
